/**
 * SubscriptionCard — displays a single Zoho subscription.
 *
 * Props:
 *   subscription  {object}   — subscription data from API
 *   isActive      {boolean}  — true for active statuses (Live, Trial, Future, Non Renewing)
 *
 * Active cards show:   plan_name, subscription_status, subscription_number,
 *                      payment_mode, next_bill, next_bill_amount, subscription_end
 *
 * Non-active cards show: plan_name, subscription_status, subscription_number, cancel_date
 */

const currencyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const STATUS_BADGE_MAP = {
  'Live':           'bp-badge--success',
  'Trial':          'bp-badge--info',
  'Future':         'bp-badge--info',
  'Non Renewing':   'bp-badge--warning',
  'Trial Expired':  'bp-badge--muted',
  'Canceled':       'bp-badge--danger',
  'Expired':        'bp-badge--danger',
};

function formatDate(dateStr) {
  if (!dateStr || dateStr === '0000-00-00 00:00:00') return 'N/A';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCurrency(val) {
  const num = parseFloat(val);
  if (isNaN(num) || num === 0) return 'N/A';
  return currencyFmt.format(num);
}

const SubscriptionCard = ({ subscription, isActive }) => {
  const badgeClass = STATUS_BADGE_MAP[subscription.subscription_status] || 'bp-badge--muted';

  return (
    <div className={`bp-sub-card${isActive ? '' : ' bp-sub-card--inactive'}`}>
      <div className="bp-sub-card__header">
        <h3 className="bp-sub-card__plan">{subscription.plan_name}</h3>
        <span className={`bp-badge ${badgeClass}`}>
          <span className="bp-badge__dot" aria-hidden="true" />
          {subscription.subscription_status}
        </span>
      </div>

      {isActive ? (
        /* ── Active layout ── */
        <div className="bp-sub-card__grid">
          <div className="bp-sub-card__item">
            <span className="bp-sub-card__label">Subscription #</span>
            <span className="bp-sub-card__value">{subscription.subscription_number}</span>
          </div>
          <div className="bp-sub-card__item">
            <span className="bp-sub-card__label">Auto Pay</span>
            {subscription.payment_mode === 'Auto-charge Enabled' ? (
              <span className="bp-sub-card__value bp-autopay bp-autopay--enabled">
                <svg className="bp-autopay__icon" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                </svg>
                Enabled
              </span>
            ) : (
              <span className="bp-sub-card__value bp-autopay bp-autopay--disabled">
                Not Enabled
              </span>
            )}
          </div>
          <div className="bp-sub-card__item">
            <span className="bp-sub-card__label">Next Billing Date</span>
            <span className="bp-sub-card__value">{formatDate(subscription.next_bill)}</span>
          </div>
          <div className="bp-sub-card__item">
            <span className="bp-sub-card__label">Next Bill Amount</span>
            <span className="bp-sub-card__value">{formatCurrency(subscription.next_bill_amount)}</span>
          </div>
          <div className="bp-sub-card__item">
            <span className="bp-sub-card__label">Subscription End</span>
            <span className="bp-sub-card__value">{formatDate(subscription.subscription_end)}</span>
          </div>
        </div>
      ) : (
        /* ── Non-active layout ── */
        <div className="bp-sub-card__grid">
          <div className="bp-sub-card__item">
            <span className="bp-sub-card__label">Subscription #</span>
            <span className="bp-sub-card__value">{subscription.subscription_number}</span>
          </div>
          <div className="bp-sub-card__item">
            <span className="bp-sub-card__label">Cancel Date</span>
            <span className="bp-sub-card__value">{formatDate(subscription.cancel_date)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionCard;
